import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Gift, Share2, Copy, Check, Apple, Play as PlayIcon, Users } from 'lucide-react';
import { toast } from 'sonner';
import { shareContent } from '../utils/shareContent';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

// 13-language mini dictionary for the referral / parrainage section.
const R_L = {
  fr: { title: 'Parrainage', desc: "Invite tes amis à rejoindre Destine. Partage ton lien depuis les autres apps du téléphone.", cta: "Partager l'app", copy: 'Copier le lien', copied: 'Lien copié !', chooseStore: 'Choisir la version à partager', ios: 'Version iPhone / iPad', android: 'Version Android', close: 'Fermer', shareText: "Rejoins-moi sur Destine, le premier réseau social vocal du cinéma !", invited: 'ami(s) invité(s)' },
  en: { title: 'Refer friends', desc: 'Invite your friends to join Destine. Share your link through any other app on your phone.', cta: 'Share the app', copy: 'Copy link', copied: 'Link copied!', chooseStore: 'Choose which version to share', ios: 'iPhone / iPad version', android: 'Android version', close: 'Close', shareText: 'Join me on Destine, the first voice-based social network for cinema!', invited: 'friend(s) invited' },
  es: { title: 'Recomendaciones', desc: 'Invita a tus amigos a unirse a Destine. Comparte tu enlace en cualquier otra app de tu teléfono.', cta: 'Compartir la app', copy: 'Copiar enlace', copied: '¡Enlace copiado!', chooseStore: 'Elige qué versión compartir', ios: 'Versión iPhone / iPad', android: 'Versión Android', close: 'Cerrar', shareText: 'Únete a mí en Destine, ¡la primera red social de voz del cine!', invited: 'amigo(s) invitado(s)' },
  pt: { title: 'Indicações', desc: 'Convida os teus amigos para o Destine. Partilha o teu link em qualquer outra app.', cta: 'Partilhar a app', copy: 'Copiar link', copied: 'Link copiado!', chooseStore: 'Escolha a versão a partilhar', ios: 'Versão iPhone / iPad', android: 'Versão Android', close: 'Fechar', shareText: 'Junta-te a mim no Destine, a primeira rede social vocal do cinema!', invited: 'amigo(s) convidado(s)' },
  de: { title: 'Freunde einladen', desc: 'Lade deine Freunde zu Destine ein. Teile deinen Link über andere Apps auf deinem Handy.', cta: 'App teilen', copy: 'Link kopieren', copied: 'Link kopiert!', chooseStore: 'Wähle die zu teilende Version', ios: 'iPhone / iPad Version', android: 'Android Version', close: 'Schließen', shareText: 'Komm zu mir auf Destine – dem ersten sprachbasierten Kino-Netzwerk!', invited: 'Freund(e) eingeladen' },
  it: { title: 'Invita amici', desc: 'Invita i tuoi amici su Destine. Condividi il link tramite qualsiasi altra app del telefono.', cta: "Condividi l'app", copy: 'Copia link', copied: 'Link copiato!', chooseStore: 'Scegli quale versione condividere', ios: 'Versione iPhone / iPad', android: 'Versione Android', close: 'Chiudi', shareText: 'Unisciti a me su Destine, il primo social vocale del cinema!', invited: 'amico/i invitato/i' },
  ru: { title: 'Приглашение', desc: 'Пригласите друзей в Destine. Поделитесь ссылкой через любое приложение на телефоне.', cta: 'Поделиться приложением', copy: 'Копировать ссылку', copied: 'Ссылка скопирована!', chooseStore: 'Выберите версию для отправки', ios: 'Версия iPhone / iPad', android: 'Версия Android', close: 'Закрыть', shareText: 'Присоединяйся ко мне в Destine — первой голосовой соцсети о кино!', invited: 'друг(а) приглашено' },
  ar: { title: 'دعوة الأصدقاء', desc: 'ادعُ أصدقاءك للانضمام إلى Destine. شارك رابطك عبر أي تطبيق آخر على هاتفك.', cta: 'شارك التطبيق', copy: 'نسخ الرابط', copied: 'تم نسخ الرابط!', chooseStore: 'اختر الإصدار الذي تريد مشاركته', ios: 'إصدار iPhone / iPad', android: 'إصدار Android', close: 'إغلاق', shareText: 'انضم إليّ في Destine، أوّل شبكة اجتماعية صوتية للسينما!', invited: 'صديق/أصدقاء مدعوّون' },
  zh: { title: '邀请好友', desc: '邀请你的好友加入 Destine。通过手机上任意其他 App 分享链接。', cta: '分享应用', copy: '复制链接', copied: '链接已复制！', chooseStore: '选择要分享的版本', ios: 'iPhone / iPad 版本', android: 'Android 版本', close: '关闭', shareText: '来 Destine 一起玩——首个专注电影的语音社交网络！', invited: '位好友已邀请' },
  ja: { title: '友達を招待', desc: '友達を Destine に招待しよう。他のアプリからリンクをシェアできます。', cta: 'アプリを共有', copy: 'リンクをコピー', copied: 'リンクをコピーしました！', chooseStore: '共有するバージョンを選択', ios: 'iPhone / iPad 版', android: 'Android 版', close: '閉じる', shareText: 'Destine に参加しよう — 映画のための音声SNS！', invited: '人の友達を招待済み' },
  hi: { title: 'रेफ़र करें', desc: 'अपने दोस्तों को Destine पर आमंत्रित करें। किसी भी अन्य ऐप से लिंक साझा करें।', cta: 'ऐप साझा करें', copy: 'लिंक कॉपी करें', copied: 'लिंक कॉपी हुआ!', chooseStore: 'साझा करने के लिए वर्शन चुनें', ios: 'iPhone / iPad वर्शन', android: 'Android वर्शन', close: 'बंद करें', shareText: 'Destine पर मुझसे जुड़ें — सिनेमा का पहला वॉइस सोशल नेटवर्क!', invited: 'दोस्त आमंत्रित' },
  nl: { title: 'Vrienden uitnodigen', desc: 'Nodig je vrienden uit voor Destine. Deel je link via elke andere app op je telefoon.', cta: 'App delen', copy: 'Link kopiëren', copied: 'Link gekopieerd!', chooseStore: 'Kies welke versie je wilt delen', ios: 'iPhone / iPad-versie', android: 'Android-versie', close: 'Sluiten', shareText: 'Word lid bij Destine, het eerste spraaksociale netwerk voor cinema!', invited: 'vriend(en) uitgenodigd' },
  sw: { title: 'Alika marafiki', desc: 'Alika marafiki zako kujiunga na Destine. Shiriki kiungo chako kupitia app nyingine yoyote kwenye simu.', cta: 'Shiriki app', copy: 'Nakili kiungo', copied: 'Kiungo kimenakiliwa!', chooseStore: 'Chagua toleo la kushiriki', ios: 'Toleo la iPhone / iPad', android: 'Toleo la Android', close: 'Funga', shareText: 'Jiunge nami kwenye Destine, mtandao wa kwanza wa sauti kwa sinema!', invited: 'marafiki walioalikwa' },
  ht: { title: 'Envite zanmi ou yo', desc: 'Envite zanmi ou yo pou yo rantre nan Destine. Pataje lyen ou nan lòt apps sou telefòn nan.', cta: "Pataje app la", copy: 'Kopye lyen', copied: 'Lyen kopye!', chooseStore: 'Chwazi vèsyon pou pataje', ios: 'Vèsyon iPhone / iPad', android: 'Vèsyon Android', close: 'Fèmen', shareText: 'Vin jwenn mwen sou Destine, premye rezo sosyal vokal sinema a!', invited: 'zanmi(y) envite' },
};

// Public store URLs for the app (bump these when store IDs change).
const IOS_URL = 'https://apps.apple.com/app/destine-studio/id6748186596';
const ANDROID_URL = 'https://play.google.com/store/apps/details?id=com.destinestudio.app';

const ReferralSection = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const l = R_L[language] || R_L.fr;
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [invitedCount, setInvitedCount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get('/api/referrals/my-count');
        if (!cancelled) setInvitedCount(r.data?.count ?? 0);
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const shareStore = async (url, storeName) => {
    // Optionally append the user's username as a referral tag (informational only,
    // since referral is free — no reward tracking needed for now).
    const finalUrl = user?.username ? `${url}?ref=${encodeURIComponent(user.username)}` : url;
    try {
      const res = await shareContent({
        title: 'Destine',
        text: l.shareText,
        url: finalUrl,
        dialogTitle: `${l.cta} · ${storeName}`,
      });
      if (res.shared && !res.cancelled) setOpen(false);
    } catch (e) {
      toast.error('Error');
    }
  };

  const copyLink = async (url) => {
    const finalUrl = user?.username ? `${url}?ref=${encodeURIComponent(user.username)}` : url;
    try {
      await navigator.clipboard.writeText(finalUrl);
      setCopied(true);
      toast.success(l.copied);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      toast.error('Error');
    }
  };

  return (
    <div className="bg-white/5 rounded-2xl border border-white/10 p-6" data-testid="referral-section">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-600">
          <Gift className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-semibold">{l.title}</h2>
          <p className="text-xs text-gray-400">{l.desc}</p>
        </div>
      </div>

      <button
        onClick={() => setOpen(true)}
        className="w-full mt-3 px-4 py-3 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-600 hover:from-emerald-600 hover:to-cyan-700 font-semibold flex items-center justify-center gap-2"
        data-testid="referral-share-btn"
      >
        <Share2 className="w-4 h-4" /> {l.cta}
      </button>

      {invitedCount !== null && (
        <div className="mt-3 flex items-center justify-center gap-2 text-sm text-emerald-300" data-testid="referral-count">
          <Users className="w-4 h-4" />
          <span className="font-semibold">{invitedCount}</span>
          <span className="text-gray-400">{l.invited}</span>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-md bg-gray-900 border border-white/10 rounded-3xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <h3 className="font-bold text-lg mb-1">{l.chooseStore}</h3>
              <p className="text-xs text-gray-400 mb-5">{l.desc}</p>

              <div className="space-y-2">
                <button onClick={() => shareStore(IOS_URL, 'iOS')} className="w-full p-3 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 flex items-center gap-3" data-testid="referral-share-ios">
                  <div className="p-2 rounded-full bg-white/10"><Apple className="w-5 h-5" /></div>
                  <span className="flex-1 text-left font-semibold text-sm">{l.ios}</span>
                  <Share2 className="w-4 h-4 text-emerald-400" />
                </button>
                <button onClick={() => shareStore(ANDROID_URL, 'Android')} className="w-full p-3 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 flex items-center gap-3" data-testid="referral-share-android">
                  <div className="p-2 rounded-full bg-white/10"><PlayIcon className="w-5 h-5" /></div>
                  <span className="flex-1 text-left font-semibold text-sm">{l.android}</span>
                  <Share2 className="w-4 h-4 text-emerald-400" />
                </button>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <button onClick={() => copyLink(IOS_URL)} className="px-3 py-2 rounded-full bg-white/5 hover:bg-white/10 text-xs flex items-center justify-center gap-2" data-testid="referral-copy-ios">
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} iOS
                </button>
                <button onClick={() => copyLink(ANDROID_URL)} className="px-3 py-2 rounded-full bg-white/5 hover:bg-white/10 text-xs flex items-center justify-center gap-2" data-testid="referral-copy-android">
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} Android
                </button>
              </div>

              <button onClick={() => setOpen(false)} className="w-full mt-4 px-4 py-2.5 rounded-full bg-white/5 hover:bg-white/10 text-sm">
                {l.close}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralSection;
